import NewLookPlanFeatures from "features/settings/Subscription/NewLook/NewLookPlanFeatures";
import Title from "common/Typography/Title";
import styled, { css, useTheme } from "styled-components";
import { formatMoney } from "features/settings/Subscription/NewLook/formatMoney.ts";
import Text from "common/Typography/Text";
import CheckCircleFilled from "@ant-design/icons/CheckCircleFilled";
import LeftOutlined from "@ant-design/icons/LeftOutlined";
import React, { useContext, useEffect } from "react";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { typography } from "@shared/common/Themes/typography";
import { InitialPrice } from "features/settings/common/styles";
import Flex from "common/Boxes/Flex";
import { toFixed } from "@shared/utils/numbers.ts";
import Col from "antd/es/col";
import Row from "antd/es/row";
import DurationRadio from "features/settings/Subscription/DurationRadio";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import SummaryRow from "features/settings/Subscription/SummaryRow";
import usePlanForm from "features/settings/Subscription/usePlanForm";
import {
  getNewLookQuotaPlanFeature,
  getQuotaPlanFeature,
} from "features/settings/Subscription/utils";
import usePlanFeatures from "@shared/features/settings/subscription/hooks/usePlanFeatures.ts";
import { SMALL_MOBILE_CHECKOUT_HEIGHT } from "features/settings/common/constants.ts";
import { useIsSmallMobileCheckout } from "features/settings/common/hooks.tsx";
import { LanguageNameSpaces } from "@shared/types";
import TextWithIcon from "common/Text/TextWithIcon.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import ApiSlotsSelector from "features/settings/Subscription/ApiSlotsSelector";
import ApiSlotsPriceRowComponent from "features/settings/Subscription/ApiSlotsPriceRowComponent";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context";
import { getIsApiSlotsPriceRowVisible } from "features/settings/common/apiSlotsValidation.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";

const PlanForm = () => {
  const {
    plan,
    analyticsInvoiceType,
    promoCodePrices,
    isExtend,
    isBottomSummary,
    isUpgradePricesLoading,
    numApiSlots,
    additionalSlotPrice,
    previousStep,
    paymentMethod,
    cryptoTotalPrice,
    isStandaloneApiSlots,
  } = useContext(BuySubscriptionContext)!;

  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { analytics } = useContext(AnalyticsContext);
  const formData = usePlanForm({
    paymentMethod,
    cryptoTotalPrice,
  });
  const { isNewLook } = useTheme();
  const { getPlanFeatures } = usePlanFeatures({
    plan,
    getQuotaPlanFeature: isNewLook
      ? getNewLookQuotaPlanFeature
      : getQuotaPlanFeature,
  });
  const isSmallMobile = useIsSmallMobileCheckout();

  useEffect(() => {
    analytics?.purchasePlanDurationPageView({
      subscriptionInvoiceType: analyticsInvoiceType,
      // @ts-ignore
      subscriptionInvoicePlan: plan?.subscription_type,
    });
  }, [analytics]);

  if (!formData) return null;

  const {
    extraFeeData,
    totalData,
    nextRenewalCostData,
    upgradeRowData,
    totalCryptoData,
    durationSelectionData,
  } = formData;

  const planFeatures = isNewLook ? (
    <NewLookPlanFeatures plan={plan} />
  ) : (
    <Row gutter={[12, 0]}>
      {getPlanFeatures()?.map((feature, index) => (
        <Col span={12} key={index}>
          <FeatureText as="div">
            <CheckIcon />
            {feature}
          </FeatureText>
        </Col>
      ))}
    </Row>
  );

  const durationSelection = durationSelectionData.show && (
    <>
      {!isBottomSummary && (
        <Title
          level={2}
          $weight={typography.fontWeights.SEMI_BOLD}
          // The new look: a section title (panel-title role, 14px).
          $size={isNewLook ? typography.fontSizes.M : undefined}
        >
          {durationSelectionData.title}
        </Title>
      )}
      {durationSelectionData.durations.map((subscriptionDuration) => {
        return (
          <DurationRadio
            key={subscriptionDuration}
            duration={subscriptionDuration}
            promoCode={promoCodePrices?.[subscriptionDuration]}
            hideRadio={durationSelectionData.hideRadio}
          />
        );
      })}
    </>
  );

  const extraFeeRow = extraFeeData.show && (
    <SummaryRow
      label={extraFeeData.title}
      price={extraFeeData.price}
      style={isBottomSummary ? { marginTop: 8 } : undefined}
    />
  );

  const totalCryptoRow = totalCryptoData.show && (
    <Flex style={{ marginTop: 8 }}>
      <Title level={isSmallMobile ? 4 : 3}>{totalCryptoData.title}</Title>
      <Flex>
        <TotalPrice as="div">
          <TextWithIcon
            text={totalCryptoData.price}
            iconLink={paymentMethod?.icon_url}
          />
        </TotalPrice>
      </Flex>
    </Flex>
  );

  const total = (
    <Flex>
      <Title level={isSmallMobile ? 4 : 3}>{totalData.title}</Title>
      <Flex>
        {totalData.showInitial && (
          <InitialPrice>
            {isNewLook
              ? `$${formatMoney(totalData.initialPrice)}`
              : totalData.initialPrice}
          </InitialPrice>
        )}
        <TotalPrice>
          {isNewLook
            ? `$${formatMoney(totalData.finalPrice)}`
            : totalData.finalPrice}
        </TotalPrice>
      </Flex>
    </Flex>
  );

  const nextRenewalCostLabel = (
    <Flex>
      {nextRenewalCostData.title}
      {nextRenewalCostData.showHelperTooltip && (
        <HelperTooltip {...nextRenewalCostData.helperTooltipProps} />
      )}
    </Flex>
  );

  const renewalBreakdown = nextRenewalCostData.renewalBreakdownData ? (
    <RenewalBreakdownContainer>
      <RenewalBreakdownSection>
        <RenewalBreakdownSectionLabel>Base Plan</RenewalBreakdownSectionLabel>
        <RenewalBreakdownRow>
          <RenewalBreakdownMuted>
            {nextRenewalCostData.renewalBreakdownData.planName} Plan
          </RenewalBreakdownMuted>
          <span>
            ${toFixed(nextRenewalCostData.renewalBreakdownData.planPrice, 2)}
          </span>
        </RenewalBreakdownRow>
        <RenewalBreakdownSubtext>
          {nextRenewalCostData.renewalBreakdownData.baseNumberOfClients ??
            nextRenewalCostData.renewalBreakdownData.totalSlots -
              (nextRenewalCostData.renewalBreakdownData.numApiSlots ?? 0)}{" "}
          included API slots
        </RenewalBreakdownSubtext>
      </RenewalBreakdownSection>
      <RenewalBreakdownDivider />
      <RenewalBreakdownSection>
        <RenewalBreakdownSectionLabel>
          Additional Slots
        </RenewalBreakdownSectionLabel>
        <RenewalBreakdownRow>
          <RenewalBreakdownMuted>
            {nextRenewalCostData.renewalBreakdownData.baseNumberOfClients !==
            undefined
              ? nextRenewalCostData.renewalBreakdownData.totalSlots -
                nextRenewalCostData.renewalBreakdownData.baseNumberOfClients
              : nextRenewalCostData.renewalBreakdownData.numApiSlots}{" "}
            API Slots
          </RenewalBreakdownMuted>
          <span>
            $
            {toFixed(
              nextRenewalCostData.renewalBreakdownData.apiSlotsRecurringPayment,
              2
            )}
          </span>
        </RenewalBreakdownRow>
      </RenewalBreakdownSection>
      <RenewalBreakdownDivider />
      <RenewalBreakdownTotalRow>
        <RenewalBreakdownSectionLabel>
          Total ({nextRenewalCostData.renewalBreakdownData.totalSlots} slots)
        </RenewalBreakdownSectionLabel>
        <RenewalBreakdownTotalAmount>
          ${toFixed(nextRenewalCostData.price, 2)}
        </RenewalBreakdownTotalAmount>
      </RenewalBreakdownTotalRow>
    </RenewalBreakdownContainer>
  ) : undefined;

  const nextRenewalCostRow = nextRenewalCostData.show && (
    <div style={{ marginBottom: 16 }}>
      <SummaryRow
        label={nextRenewalCostLabel}
        price={nextRenewalCostData.price}
        style={{ marginBottom: 0 }}
        tooltipContent={renewalBreakdown}
      />
      <Flex>
        <Text type="secondary">{nextRenewalCostData.userExpirationDate}</Text>
        <Text type="secondary">{nextRenewalCostData.billedDuration}</Text>
      </Flex>
    </div>
  );

  const upgradeRow = upgradeRowData.show && (
    <SummaryRow
      label={upgradeRowData.title}
      initialPrice={upgradeRowData.initialPrice}
      price={upgradeRowData.price}
      style={{ marginTop: (numApiSlots ?? 0) > 0 ? 8 : 0 }}
    />
  );

  const apiSlotsSelector = additionalSlotPrice ? (
    <ApiSlotsSelector isBottomSummary={isBottomSummary} isLocked={isExtend} />
  ) : null;

  const showAdditionalDivider =
    upgradeRowData.show || (apiSlotsSelector && (numApiSlots ?? 0) > 0);

  const isApiSlotsPriceRowVisible = getIsApiSlotsPriceRowVisible({
    numApiSlots,
    additionalSlotPrice,
    isStandaloneApiSlots,
  });
  const showPreUpgradeRowDivider =
    (isApiSlotsPriceRowVisible || !!apiSlotsSelector) && upgradeRowData.show;

  if (isBottomSummary) {
    return (
      <>
        <Divider />
        <MobileWrapper
          style={durationSelection ? { marginTop: -12 } : undefined}
        >
          {durationSelection}
          {durationSelection && <Divider />}
          {apiSlotsSelector}
          {!showAdditionalDivider && apiSlotsSelector && <Divider />}
          <LoadingSpinnerOverlay isLoading={isUpgradePricesLoading} wrap={true}>
            <ApiSlotsPriceRowComponent />
            {showPreUpgradeRowDivider && <Divider />}
            {upgradeRow}
            {showAdditionalDivider && <Divider />}
            {nextRenewalCostRow}
            {nextRenewalCostData.show && <Divider />}
            {extraFeeRow}
            {total}
            {totalCryptoRow}
          </LoadingSpinnerOverlay>
        </MobileWrapper>
      </>
    );
  }

  return (
    <>
      {/* The new look shows the title and back link above the cards. */}
      {!isNewLook && (
        <PlanTitle>
          <LeftArrow onClick={previousStep} />
          {plan?.subscription_type} {t("plan_form.plan")}
        </PlanTitle>
      )}
      {planFeatures}
      {durationSelection && <Divider />}
      {durationSelection}
      {(durationSelection || apiSlotsSelector) && <Divider />}
      {apiSlotsSelector}
      {!showAdditionalDivider && apiSlotsSelector && <Divider />}
      <LoadingSpinnerOverlay isLoading={isUpgradePricesLoading} wrap={true}>
        <ApiSlotsPriceRowComponent />
        {showPreUpgradeRowDivider && <Divider />}
        {upgradeRow}
        {showAdditionalDivider && <Divider />}
        {nextRenewalCostRow}
        {nextRenewalCostData.show && <Divider />}
        {extraFeeRow}
        {total}
        {totalCryptoRow}
      </LoadingSpinnerOverlay>
    </>
  );
};

export default PlanForm;

const PlanTitle = styled(Text)`
  font-size: ${(p) => p.theme.typography.fontSizes.XXL};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  margin-bottom: 20px;
  display: flex;
  align-items: center;
`;
const LeftArrow = styled(LeftOutlined)`
  font-size: 20px;
  padding: 8px;
`;
const FeatureText = styled.div`
  display: flex;
  align-items: baseline;
  padding: 6px 0;
  color: ${(p) => p.theme.colors.textColor};
`;
const Divider = styled.div`
  margin: 16px 0;
  width: 100%;
  height: 1px;
  background: ${(p) => p.theme.colors.lightestBorderGrey};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin: 20px 0;
      background: ${p.theme.colors.cardBorderColor};
    `}
`;
const CheckIcon = styled(CheckCircleFilled)`
  color: ${(p) => p.theme.colors.primaryBlue};
  margin-right: 8px;
  top: 2px;
  position: relative;
  font-size: ${(p) => p.theme.typography.fontSizes.L};,
`;
const TotalPrice = styled(Text)`
  font-size: ${(p) => p.theme.typography.fontSizes.L};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  @media (max-height: ${SMALL_MOBILE_CHECKOUT_HEIGHT}) {
    font-size: ${(p) => p.theme.typography.fontSizes.M};
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        color: ${p.theme.colors.titleColor};
        font-size: 20px;
        font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
        letter-spacing: -0.01em;
        font-variant-numeric: tabular-nums;
      }
    `}
`;
const MobileWrapper = styled.div`
  width: 100%;
  padding: 0 16px 16px 16px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 0;
    `}
`;
const RenewalBreakdownContainer = styled.div`
  min-width: 220px;
  padding: 4px 0;
`;
const RenewalBreakdownSection = styled.div`
  padding: 4px 0;
`;
const RenewalBreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 12px;
`;
const RenewalBreakdownSectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 2px;
`;
const RenewalBreakdownSubtext = styled.div`
  font-size: 11px;
  opacity: 0.65;
  margin-bottom: 2px;
`;
const RenewalBreakdownMuted = styled.span``;
const RenewalBreakdownDivider = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  margin: 4px 0;
`;
const RenewalBreakdownTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 0;
`;
const RenewalBreakdownTotalAmount = styled.span`
  font-weight: 700;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
    `}
`;
